import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";
import { z } from "zod";
import { AlchemaLogo } from "~/components/AlchemaLogo";

const resetSearchSchema = z.object({
  token: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute("/auth/reset-password")({
  validateSearch: (search) => resetSearchSchema.parse(search),
  component: ResetPasswordPage,
});

export function ResetPasswordPage() {
  const { token, error: callbackError } = Route.useSearch();
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("This reset link is invalid or expired.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, newPassword: password }),
      });
      const payload = await response.json().catch(() => null) as { message?: string } | null;
      if (!response.ok) {
        throw new Error(payload?.message || "Unable to reset password.");
      }
      setSuccess(true);
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to reset password.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-md space-y-6 animate-fade-up">
        <Link to="/" className="flex justify-center no-underline" aria-label="Alchema home">
          <AlchemaLogo style={{ fontSize: "1.5rem" }} />
        </Link>

        <div className="card p-8 space-y-6" style={{ borderRadius: "var(--radius-xl, 1.25rem)" }}>
          <div className="text-center">
            <h1 className="font-semibold tracking-tight" style={{ fontSize: "var(--text-2xl, 1.5rem)", color: "var(--text)", letterSpacing: "var(--tracking-snug, -0.015em)" }}>
              Set a new password
            </h1>
            <p className="mt-1.5 text-sm" style={{ color: "var(--text-muted)" }}>
              Choose a strong password for your account.
            </p>
          </div>

          {callbackError === "INVALID_TOKEN" && (
            <div role="alert" className="rounded-lg border p-3 text-sm" style={{ borderColor: "var(--error)", background: "var(--error-subtle)", color: "var(--error)" }}>
              This reset link is invalid or expired.
            </div>
          )}

          {success ? (
            <div className="space-y-4">
              <div role="status" className="rounded-lg border p-3 text-sm" style={{ borderColor: "var(--success)", background: "var(--success-subtle, rgba(22, 163, 74, 0.1))", color: "var(--success)" }}>
                Your password has been reset successfully.
              </div>
              <Link to="/auth/login" className="btn btn-accent w-full text-center no-underline">
                Continue to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="reset-password-form" className="space-y-4">
              {error && (
                <div role="alert" className="rounded-lg border p-3 text-sm" style={{ borderColor: "var(--error)", background: "var(--error-subtle)", color: "var(--error)" }}>
                  {error}
                </div>
              )}

              <div className="space-y-1.5">
                <label htmlFor="password" className="block text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
                  New password
                </label>
                <input
                  id="password"
                  type="password"
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="input"
                  placeholder="At least 8 characters"
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="confirm-password" className="block text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="input"
                />
              </div>

              <button type="submit" disabled={loading || !token} className="btn btn-accent w-full disabled:opacity-50">
                {loading ? "Resetting password..." : "Reset password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
